import { useEffect, useState, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { IS_NATIVE } from '@/hooks/useNativePlatform';
import { supabase } from '@/integrations/supabase/client';

import { decideUpdate, type VersionConfig } from '@/lib/appUpdateDecision';
export { compareVersions, isVersionOlder, decideUpdate, type VersionConfig } from '@/lib/appUpdateDecision';

const DISMISS_KEY = 'app_update_dismissed_version';

export const useAppUpdatePrompt = () => {
  const [config, setConfig] = useState<VersionConfig | null>(null);
  const [kind, setKind] = useState<'none' | 'optional' | 'required'>('none');

  const checkVersion = useCallback(async () => {
    if (!IS_NATIVE) return;
    const platform = Capacitor.getPlatform();
    if (platform !== 'ios' && platform !== 'android') return;
    try {
      const [{ version }, { data }] = await Promise.all([
        App.getInfo(),
        (supabase as any)
          .from('app_version_config')
          .select('min_version,latest_version,store_url,title,message,enabled')
          .eq('platform', platform)
          .maybeSingle(),
      ]);
      const cfg = (data as VersionConfig) ?? null;
      const decision = decideUpdate(version, cfg);
      let next = decision.kind;
      if (next === 'optional' && cfg) {
        try {
          if (localStorage.getItem(DISMISS_KEY) === cfg.latest_version) next = 'none';
        } catch { /* ignore */ }
      }
      setConfig(cfg);
      setKind(next);
    } catch {
      // Fail silently — never block the app if the check fails
    }
  }, []);

  useEffect(() => {
    checkVersion();
    if (!IS_NATIVE) return;
    const listener = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) checkVersion();
    });
    return () => { listener.then((l) => l.remove()); };
  }, [checkVersion]);

  const dismiss = useCallback(() => {
    if (kind !== 'optional' || !config) return;
    try { localStorage.setItem(DISMISS_KEY, config.latest_version); } catch { /* ignore */ }
    setKind('none');
  }, [kind, config]);

  return { showPrompt: kind !== 'none', required: kind === 'required', config, dismiss };
};
